import { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import type {
  ArticleWholesaleListPrice,
  ArticleWholesaleSupplierSpend,
} from "../../api/articles";
import {
  RawRecordModal,
  type RawRecordModalData,
} from "../../components/RawRecordModal";
import { filterColor } from "../../lib/filterColors";
import { formatDate, formatEuro, formatPercent } from "../../lib/format";

import { formatListPrice } from "./catalog";

export { RawRecordModal };
export type { RawRecordModalData };

export function WholesaleSupplierBlock({
  evidenceCount,
  supplierRollup,
  supplierFilter,
  isLoading,
  onSupplierChange,
}: {
  evidenceCount: number;
  supplierRollup: ArticleWholesaleSupplierSpend[];
  supplierFilter: string | null;
  isLoading: boolean;
  onSupplierChange: (supplier: string | null) => void;
}) {
  if (isLoading) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
        Lade Großhandelsdaten …
      </section>
    );
  }

  if (supplierRollup.length === 0) return null;

  return (
    <section className="min-w-0 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-sm">
      <WholesaleSupplierSpendDonut
        items={supplierRollup}
        value={supplierFilter}
        onChange={onSupplierChange}
      />
      <div className="text-xs text-slate-500">
        {evidenceCount.toLocaleString("de-AT")} UGL-Zeilen als Nachweis in der
        Historie{supplierFilter ? ` für ${supplierFilter}` : ""}
      </div>
    </section>
  );
}

export function WholesaleListPriceBlock({
  listPrice,
  isLoading,
  onOpenRawRecord,
}: {
  listPrice: ArticleWholesaleListPrice | null;
  isLoading: boolean;
  onOpenRawRecord: (record: RawRecordModalData) => void;
}) {
  if (isLoading) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
        Lade Listenpreis …
      </section>
    );
  }

  if (!listPrice) return null;

  const sourceLabel =
    listPrice.source_kind === "datanorm_price" ? "DATANORM" : "Katalog";
  const listPriceLabel = formatListPrice(
    listPrice.list_unit_price,
    listPrice.currency,
    null,
  );

  return (
    <section className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Listenpreis ({sourceLabel})
        </div>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-lg font-semibold tabular-nums text-slate-900">
            {listPriceLabel ?? "–"}
          </span>
          <span className="text-xs text-slate-500">
            {[
              listPrice.supplier,
              listPrice.observed_date
                ? `gültig ab ${formatDate(listPrice.observed_date)}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      </div>
      {listPrice.raw_record ? (
        <button
          type="button"
          onClick={() =>
            onOpenRawRecord({
              title: `${sourceLabel}-Rohdaten`,
              sourceKind: listPrice.source_kind,
              supplier: listPrice.supplier,
              sourceFile: listPrice.source_file,
              sourceRecordRow: listPrice.source_record_row,
              rawRecord: listPrice.raw_record,
            })
          }
          className="shrink-0 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          {sourceLabel}-Rohdaten
        </button>
      ) : null}
    </section>
  );
}

export function WholesaleSupplierSpendDonut({
  items,
  value,
  onChange,
}: {
  items: ArticleWholesaleSupplierSpend[];
  value: string | null;
  onChange: (supplier: string | null) => void;
}) {
  const rows = useMemo(
    () =>
      items.map((item) => ({
        supplier: item.supplier,
        lineCount: Number(item.line_count ?? 0),
        totalQty: Number(item.total_qty ?? 0),
        totalSpend: Number(item.total_spend ?? 0),
        lastDocDate: item.last_doc_date,
        hasListPrice: item.has_list_price === true,
        color: filterColor("supplier", item.supplier),
      })),
    [items],
  );
  const positiveRows = rows.filter((row) => row.totalSpend > 0);
  const totalSpend = positiveRows.reduce((sum, row) => sum + row.totalSpend, 0);

  if (rows.length === 0) return null;

  return (
    <div className="mb-3 grid min-w-0 max-w-full grid-cols-1 gap-3 rounded-md border border-emerald-100 bg-white/70 p-2 sm:max-w-2xl sm:grid-cols-[7rem_1fr]">
      <div className="h-28">
        {totalSpend > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={positiveRows}
                dataKey="totalSpend"
                nameKey="supplier"
                innerRadius={28}
                outerRadius={48}
                stroke="#fff"
                strokeWidth={1}
              >
                {positiveRows.map((row) => (
                  <Cell
                    key={row.supplier}
                    fill={row.color}
                    opacity={value && value !== row.supplier ? 0.35 : 1}
                    onClick={() =>
                      onChange(value === row.supplier ? null : row.supplier)
                    }
                    style={{ cursor: "pointer" }}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(rawValue) => [
                  formatEuro(rawValue as number),
                  "Ausgaben",
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-slate-400">
            Keine Ausgaben
          </div>
        )}
      </div>
      <div className="min-w-0">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Großhändler-Ausgaben
        </div>
        <ul className="space-y-1">
          {rows.filter((row) => row.totalSpend > 0).map((row) => {
            const active = value === row.supplier;
            const share =
              totalSpend > 0 && row.totalSpend > 0
                ? formatPercent((row.totalSpend / totalSpend) * 100)
                : "0 %";
            return (
              <li key={row.supplier}>
                <button
                  type="button"
                  onClick={() => onChange(active ? null : row.supplier)}
                  className={`grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded px-2 py-1 text-left ${
                    active ? "bg-emerald-100" : "hover:bg-emerald-50"
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-sm"
                      style={{ backgroundColor: row.color }}
                    />
                    <span className="font-semibold text-slate-800">
                      {row.supplier}
                    </span>
                    {row.hasListPrice ? (
                      <span className="rounded border border-emerald-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                        Katalog
                      </span>
                    ) : null}
                    {row.lineCount > 0 ? (
                      <span className="truncate text-xs text-slate-500">
                        {row.lineCount.toLocaleString("de-AT")} Zeilen
                      </span>
                    ) : null}
                  </span>
                  <span className="text-right tabular-nums">
                    <span className="font-semibold text-slate-900">
                      {formatEuro(row.totalSpend)}
                    </span>
                    <span className="ml-2 text-xs text-slate-500">{share}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
